import { createColumnHelper } from '@tanstack/react-table'
import type { ParseKeys } from 'i18next'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavLink, useNavigate, useSearchParams } from 'react-router-dom'

import { byDecimal } from '@/components/data/byDecimal'
import { ColumnsMenu } from '@/components/data/ColumnsMenu'
import {
  DateRangeControl,
  isDateRange,
  type DateRange,
} from '@/components/data/DateRangeControl'
import { FacetFilter } from '@/components/data/FacetFilter'
import { RecordCard, RecordList } from '@/components/data/RecordList'
import { TableEmptyState } from '@/components/data/TableEmptyState'
import {
  toHiddenColumns,
  toVisibilityState,
  useViewState,
} from '@/components/data/useViewState'
import { Banner } from '@/components/feedback/Banner'
import { EmptyState } from '@/components/feedback/EmptyState'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Skeleton } from '@/components/feedback/Skeleton'
import { PageHeader } from '@/components/layout/PageHeader'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader, CardTitle } from '@/components/ui/Card'
import { toOptions } from '@/components/ui/control'
import { Sheet } from '@/components/ui/Sheet'
import { StatusPill } from '@/components/ui/StatusPill'
import { TabNav } from '@/components/ui/TabNav'
import { TabPanel } from '@/components/ui/TabPanel'
import { Tag } from '@/components/ui/Tag'
import { useFormat } from '@/i18n/useFormat'
import { CAP, can } from '@/lib/can'
import { cn } from '@/lib/cn'
import { formatMoney, formatMoneyString, formatNumber } from '@/lib/format'
import { refusalMessage } from '@/lib/refusals'
import { useSession } from '@/features/auth/api/useSession'
import {
  canWorkPaymentQueue,
  useAging,
  useApprovals,
  useBalances,
  useDecideApproval,
  usePayments,
  usePendingPayments,
  useRecordPayment,
  toPaymentPayload,
} from '@/features/finance/api/useFinance'
import { PaymentFormSheet } from '@/features/finance/components/FinanceFormSheets'
// Still `InvoicesPanel.tsx` on disk, and the export inside it is no longer
// called that: the tab lists a case's invoices AND its debit notes now, so a
// component named for one of the two would misdescribe half of what it renders.
// Moving the file is a rename this pass deliberately did not make while other
// work was in flight in the same tree; the next reader should do it.
import { DocumentsPanel } from '@/features/finance/components/InvoicesPanel'
import { PaymentAllocations } from '@/features/finance/components/PaymentAllocations'
import { PaymentCaseLink } from '@/features/finance/components/PaymentCaseLink'
import { PaymentStatusTag } from '@/features/finance/components/PaymentStatusTag'
import type {
  ApprovalRequest,
  CounterpartyBalance,
  Payment,
  UnallocatedOutbound,
  UninvoicedReceivable,
} from '@/types/domain'

/* Money. The page subtitle is "Who owes whom", because that is the actual
 * question — say "Money", not "Financials".
 *
 * Every surface here shares ONE date-range control (FR-53), and this page no
 * longer declares its own set of periods: the vocabulary and the control are
 * `components/data/DateRangeControl`, which the dashboard mounts too. A second
 * declaration here is exactly how this page and the dashboard came to filter
 * by different periods while looking alike.
 *
 * ── THE DENSE-TABLE LAYER, AND THE TWO TABS THAT ARE NOT TABLES ─────────────
 *
 * Story 6.5's criterion is that these views behave "as the standard dense
 * table" (UX-DR-4): sortable by any column, filters and column choices
 * persisted per surface, row click opening the record and never expanding in
 * place, virtualised above 100 rows. This page had NONE of it and the quotes
 * and cases lists had all of it, because they were built by different passes —
 * so what was needed was to adopt the existing layer (`RecordList` over
 * `DataTable`, `useViewState`, `ColumnsMenu`, `FacetFilter`), not to write a
 * second one.
 *
 * Three tabs took it: BALANCES, PAYMENTS and the paper in DOCUMENTS. Each is
 * a list of like rows, which is what a dense table is for, and the payments tab
 * had an in-place disclosure — precisely the shape the criterion names as the
 * thing not to do — so its allocations moved into a sheet.
 *
 * TWO DID NOT, and refusing was the point rather than an omission:
 *
 *   · AGING is a five-bar summary, not a list of rows. That was true of the
 *     SHAPE and is no longer true of the DATA: `GET /finance/aging` now answers
 *     `{items, rows}`, and `rows` is the per-item `AgedItem` list this file used
 *     to say no endpoint served. What is still absent is the drill-through UI —
 *     a bucket you can click into, a table under it — and that is a screen to
 *     design rather than a field to rename, so it is owned separately and this
 *     panel goes on rendering the five bars only. The claim that has to stay
 *     dead is the old one: the rows exist, and the next reader should not be
 *     told to go and add the query.
 *   · APPROVALS is a decision queue. UX-DR-20 requires the requested change
 *     shown ALONGSIDE the current value together with who is deciding, plus the
 *     reason in full and two actions — which is a card, and a dense row cannot
 *     hold it without dropping the thing the criterion asks for. Story 6.5's
 *     "rate changes" VIEW is a different surface: every entry regardless of
 *     outcome, filterable, read-only (`RateChangeHistoryEntry`, declared in
 *     `types/domain.ts` and consumed by nothing, because no endpoint serves it).
 *     That one is a table when it exists; this one is not.
 *
 * NEITHER of those two is a fixture any more: `/finance/aging` and
 * `/finance/balances` are answered by `internal/finance` over real rows, and the
 * `internal/showcase` fixtures behind them are deleted. The wire moved with
 * them — `AgingBucket.key` became `id`, `CounterpartyBalance.entityId` stopped
 * existing, `kind` lost three of its four values — and every place this page
 * still read the fixture's spelling was reading `undefined` off the live server
 * rather than failing loudly. See `types/domain.ts`, which is the authority on
 * what actually arrives.
 *
 * THE GAP IN THE SHARED LAYER THAT USED TO BE HERE IS CLOSED, and this note
 * survives only to stop the workaround being copied: `RecordList` did not
 * forward `DataTable`'s `filtered` + `onClearFilters`, so the UX-DR-17 split had
 * to be hand-built as an empty NODE that reached both halves. It forwards both
 * now, and the Documents panel passes the props. The two panels below still
 * pass nodes — they work, and rewriting a working empty state was not this
 * pass's business — but a NEW surface should pass the props. */

const TABS = [
  { key: 'balances', labelKey: 'finance.tabs.balances' },
  { key: 'aging', labelKey: 'finance.tabs.aging' },
  { key: 'payments', labelKey: 'finance.tabs.payments' },
  { key: 'approvals', labelKey: 'finance.tabs.approvals' },
  { key: 'documents', labelKey: 'finance.tabs.documents' },
] as const

type TabKey = (typeof TABS)[number]['key']

const balanceColumn = createColumnHelper<CounterpartyBalance>()

const BALANCE_COLUMN_MENU: readonly {
  id: string
  labelKey: ParseKeys
  locked?: boolean
}[] = [
  { id: 'name', labelKey: 'finance.balances.counterparty', locked: true },
  { id: 'kind', labelKey: 'common.fields.type' },
  { id: 'receivable', labelKey: 'finance.balances.receivable' },
  { id: 'payable', labelKey: 'finance.balances.payable' },
  { id: 'net', labelKey: 'finance.balances.net' },
  { id: 'oldestDays', labelKey: 'finance.balances.oldest' },
]

const BALANCE_LOCKED: readonly string[] = BALANCE_COLUMN_MENU.filter(
  (entry) => entry.locked,
).map((entry) => entry.id)

/* TWO kinds, not four. Finance keys money under `client` or `participant`
   because that is the only counterparty TYPE its own tables record, and it may
   not learn the directory's vocabulary to add up money (AD-2).
   `agent`, `counter-agent` and `carrier` were the FIXTURE's words: the live read
   has never sent one, so while they sat here the facet offered three options
   that matched no row and emptied the table on click. What sort of participant a
   party is remains the directory's question, not this report's. */
const BALANCE_KINDS: readonly CounterpartyBalance['kind'][] = ['client', 'participant']

/** Where a balance row leads (S-22f): the record its `directoryId` names — the
 *  client's page for a `client` row, the counterparty's for a `participant` one.
 *  `null` for a party typed by name and linked to no record (`directoryId: ''`)
 *  and for a kind this client has no page for, so that row opens nothing. Never
 *  the row's `id`: that is an opaque `kind:uuid` handle, not a record id. */
function balanceHref(row: CounterpartyBalance): string | null {
  if (row.directoryId === '') return null
  switch (row.kind) {
    case 'client':
      return `/clients/${row.directoryId}`
    case 'participant':
      return `/counterparties/${row.directoryId}`
    default:
      return null
  }
}

/* Per-counterparty balances (FR-35).
 *
 * ONE view state for the whole tab, and N tables under it — one per issuing
 * entity, because FR-35 breaks the position down per entity and netting across
 * two of them is the merge the breakdown exists to prevent. A `useViewState` per
 * entity is not available even in principle: the entities arrive as data and a
 * hook cannot be called in a loop over them. Sharing one is also the better
 * answer — "sort by net descending" is a habit about the report, not about
 * Ashgabat, and it should hold on every card at once.
 *
 * WHAT IS STILL MISSING, and it is the server's to send: FR-35 asks for a NETTED
 * ROLLUP per entity and there is none here. It is deliberately not computed on
 * this side — summing `Number(row.net.amount)` across the rows is money
 * arithmetic in the client, which AD-9 puts server-side and in decimal, and a
 * rollup that disagreed with the ledger by a cent would be worse than an absent
 * one.
 */
/** Where «Показать» on the unallocated banner leads (S-12): the payments tab,
 *  every period, narrowed to exactly the payments the banner counted — the page
 *  maps `unallocated=1` to the server's `unallocated=out`. */
const UNALLOCATED_OUT_HREF = '/finance?tab=payments&range=all&unallocated=1'

/* The outbound payments nobody has allocated, all time (S-12, visual decision
 * 4): one info banner above the balances, the count and the server's
 * per-currency sums printed as served — never converted or added (AD-9). It
 * names nobody, so it stands even when the rows are withheld (AD-6), and it
 * moves no balance: unallocated cash is never applied (AD-11). */
function UnallocatedBanner({ summary }: { summary: UnallocatedOutbound }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  return (
    <Banner
      tone="info"
      title={t('finance.balances.unallocatedOut', {
        n: summary.count,
        amounts: summary.amounts
          .map((sum) => formatMoneyString(sum.amount, sum.currency))
          .join(' · '),
      })}
      action={
        <Button variant="secondary" size="sm" onClick={() => void navigate(UNALLOCATED_OUT_HREF)}>
          {t('finance.balances.unallocatedShow')}
        </Button>
      }
    />
  )
}

/* What the rows above leave out (N-04): instalments cut from a case's agreed
 * rate that no invoice has billed yet. A tranche names nobody (AD-26), so it
 * cannot be a counterparty row — but «По срокам» and the dashboard's
 * «К получению» count it, and a tab that said nothing would read as a total that
 * disagrees with them. The amount is the served string, printed, never summed;
 * it follows the page's period and stands over an empty or withheld list too. */
function UninvoicedBanner({ summary }: { summary: UninvoicedReceivable }) {
  const { t } = useTranslation()
  return (
    <Banner
      tone="info"
      title={t('finance.balances.uninvoiced', {
        amount: formatMoneyString(summary.amount.amount, summary.amount.currency),
      })}
    >
      {t('finance.balances.uninvoicedHint', { n: summary.count })}
    </Banner>
  )
}

function BalancesPanel({ range }: { range: DateRange }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data, isPending, isError, error, refetch } = useBalances(range)
  const [viewState, controls] = useViewState('finance.balances')

  const kindFilter = viewState.filters.kind ?? NO_FILTER
  const items = data?.items

  const rows = useMemo(() => {
    const all = items ?? []
    if (kindFilter.length === 0) return all
    return all.filter((row) => kindFilter.includes(row.kind))
  }, [items, kindFilter])

  const columnVisibility = useMemo(
    () =>
      toVisibilityState(
        viewState.hiddenColumns.filter((id) => !BALANCE_LOCKED.includes(id)),
      ),
    [viewState.hiddenColumns],
  )

  const columns = useMemo(
    () => [
      balanceColumn.accessor('name', {
        header: t('finance.balances.counterparty'),
        /* A party linked to no record (S-22f: a name the backfill could not
           resolve to exactly one) carries the picker's «как введено» beside
           its name — the reason the row opens nothing. */
        cell: (info) => (
          <span className="flex min-w-0 items-baseline gap-1.5">
            <span className="text-text-heading truncate">{info.getValue()}</span>
            {info.row.original.directoryId === '' ? (
              <span className="text-text-muted shrink-0 text-[12px] font-light">
                {t('finance.payments.counterpartyAsEntered')}
              </span>
            ) : null}
          </span>
        ),
      }),
      balanceColumn.accessor('kind', {
        header: t('common.fields.type'),
        size: 132,
        // A code on the wire; the reader's word here (F-4), and the code
        // itself for a kind this client has no word for yet.
        cell: (info) => (
          <Tag>
            {t(`finance.balances.kinds.${info.getValue()}`, { defaultValue: info.getValue() })}
          </Tag>
        ),
      }),
      balanceColumn.accessor((row) => row.receivable.amount, {
        id: 'receivable',
        header: t('finance.balances.receivable'),
        size: 128,
        meta: { numeric: true },
        sortingFn: byDecimal<CounterpartyBalance>((row) => row.receivable.amount),
        cell: (info) => formatNumber(info.getValue(), 2),
      }),
      balanceColumn.accessor((row) => row.payable.amount, {
        id: 'payable',
        header: t('finance.balances.payable'),
        size: 128,
        meta: { numeric: true },
        sortingFn: byDecimal<CounterpartyBalance>((row) => row.payable.amount),
        cell: (info) => formatNumber(info.getValue(), 2),
      }),
      balanceColumn.accessor((row) => row.net.amount, {
        id: 'net',
        header: t('finance.balances.net'),
        size: 128,
        meta: { numeric: true },
        sortingFn: byDecimal<CounterpartyBalance>((row) => row.net.amount),
        cell: (info) => (
          <span
            className={cn(
              'font-semibold',
              Number(info.getValue()) < 0 ? 'text-negative' : 'text-positive',
            )}
          >
            {formatNumber(info.getValue(), 2)}
          </span>
        ),
      }),
      balanceColumn.accessor('oldestDays', {
        header: t('finance.balances.oldest'),
        size: 132,
        meta: { numeric: true },
        /* ALWAYS PRESENT and SIGNED. The absent case this cell used to branch on
           was the fixture's; the live read sends a number on every row, so the
           `undefined` arm was dead code guarding a state that can no longer
           occur.
           NEGATIVE is the fact that replaces it: the party's earliest obligation
           has not fallen due yet, which is the least urgent row on the card and
           the exact distinction the `not-yet-due` bucket draws. So the redness
           test reads the SIGNED number — an `Math.abs` added to tidy the column
           would paint -72 the same red as 72 and put the one counterparty nobody
           needs to chase at the top of it — and the label is the aging bucket's
           own words rather than a second phrasing of the same state, because two
           strings for one fact is how two screens come to disagree in one
           language and not the other. The magnitude is dropped with it: "not due
           for 72 days" is a countdown nobody chases by, and the bar chart on the
           next tab is where not-yet-due money is actually sized. */
        cell: (info) => {
          const days = info.getValue()
          return (
            <span className={cn(days > 60 ? 'text-negative' : 'text-text-muted')}>
              {days < 0
                ? t('finance.aging.buckets.not-yet-due')
                : t('finance.balances.oldestDays', { count: days })}
            </span>
          )
        },
      }),
    ],
    [t],
  )

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  /* Keyed on the entity NAME, because that is all the wire carries: the real
     read serves `entityName` and no `entityId`, so the display text is the only
     grouping key available. This is a genuine weakness rather than a preference
     and it is worth naming here — two of our legal entities renamed to read
     alike fold into one card whose figures net across both, and nothing in the
     payload can tell them apart. Keying on the id it used to key on was worse
     than weak: no such field arrives, so every row grouped under `undefined` and
     the tab rendered ONE table netting every entity together, which is precisely
     the merge FR-35's per-entity breakdown exists to prevent.

     THE BLANK ENTITY is a decision, not a fallthrough. The server sends '' when
     a party's documents span more than one of our entities — deliberately blank
     rather than the first one found, because naming one of two would be a claim
     the data does not support — so those rows belong under no entity heading.
     They are collected into their own card, titled with the em-dash the rest of
     the product uses for a value that is not there, and sorted last so the named
     entities lead the report. The alternative, letting '' key a card like any
     other, puts an untitled card among titled ones and invites the reader to
     take it for an entity whose name failed to load; the worse alternative,
     folding those rows into a named entity's card, would show a net figure
     that silently mixed Ashgabat's receivable with Yiwu's payable. */
  const byEntity = rows.reduce<Record<string, CounterpartyBalance[]>>((acc, row) => {
    ;(acc[row.entityName] ??= []).push(row)
    return acc
  }, {})

  // `sort` is stable, so this moves the blank group to the end and leaves every
  // named entity in the order the rows arrived in.
  const entityCards = Object.entries(byEntity).sort(([a], [b]) =>
    a === '' ? 1 : b === '' ? -1 : 0,
  )

  const controlsRow = (
    <span className="flex flex-wrap items-center gap-2">
      <FacetFilter
        label={t('common.fields.type')}
        options={toOptions(BALANCE_KINDS, (kind) =>
          t(`finance.balances.kinds.${kind}`, { defaultValue: kind }),
        )}
        selected={kindFilter}
        onChange={(values) => controls.setFilter('kind', values)}
      />
      <ColumnsMenu
        columns={BALANCE_COLUMN_MENU.map((entry) => ({
          id: entry.id,
          label: t(entry.labelKey),
          locked: entry.locked,
        }))}
        hidden={viewState.hiddenColumns}
        onToggle={controls.toggleColumn}
        onReset={() => controls.setHiddenColumns([])}
      />
    </span>
  )

  /* Emptiness is decided ONCE, here, rather than per entity card: with the facet
     narrowing everything away there are no cards at all, so a per-card empty
     state would never render and the reader would be left with a blank tab. */
  /* Above the table in both branches: the summary is all-time and names
     nobody, so it is true — and served — over an empty or withheld list too. */
  const unallocatedBanner = (
    <>
      {data?.uninvoiced ? <UninvoicedBanner summary={data.uninvoiced} /> : null}
      {data?.unallocatedOutbound ? (
        <UnallocatedBanner summary={data.unallocatedOutbound} />
      ) : null}
    </>
  )

  if (rows.length === 0) {
    return (
      <div className="flex flex-col gap-3.5">
        {unallocatedBanner}
        <Card>
          <CardHeader action={controlsRow}>
            <CardTitle>{t('finance.tabs.balances')}</CardTitle>
          </CardHeader>
          <TableEmptyState
            filtered={kindFilter.length > 0 && (items ?? []).length > 0}
            onClearFilters={controls.clearFilters}
            title={t('finance.balances.emptyTitle')}
            description={t('finance.balances.emptyDescription')}
          />
        </Card>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      {unallocatedBanner}
      {entityCards.map(([entityName, entityRows], index) => {
        /* The em-dash is the product's placeholder for a value that is not
           there, and "our entity" genuinely is not there on these rows — see
           the grouping above. Resolved once and used for the heading, the
           table's accessible name and the phone card alike, so the three cannot
           come to say different things about the same group. */
        const entityLabel = entityName === '' ? t('common.empty') : entityName
        return (
        <Card key={entityName}>
          {/* The controls sit on the FIRST card only. They govern the tab, not
              one entity, and repeating them per card would read as four
              independent filters over four independent tables. */}
          <CardHeader action={index === 0 ? controlsRow : undefined}>
            <CardTitle>{entityLabel}</CardTitle>
          </CardHeader>

          <RecordList
            label={t('finance.balances.tableLabel', { entity: entityLabel })}
            data={entityRows}
            columns={columns}
            getRowId={(row) => row.id}
            renderCard={(row) => (
              <RecordCard
                id={
                  row.directoryId === '' ? (
                    <>
                      {/* Not brand blue: the id slot's colour says «opens», and
                          an unlinked row opens nothing. */}
                      <span className="text-text-heading">{row.name}</span>{' '}
                      <span className="text-text-muted text-[12px] font-light">
                        {t('finance.payments.counterpartyAsEntered')}
                      </span>
                    </>
                  ) : (
                    row.name
                  )
                }
                status={
                  <Tag>
                    {t(`finance.balances.kinds.${row.kind}`, { defaultValue: row.kind })}
                  </Tag>
                }
                primary={entityLabel}
                secondary={
                  row.oldestDays < 0
                    ? t('finance.aging.buckets.not-yet-due')
                    : t('finance.balances.oldestDays', { count: row.oldestDays })
                }
                value={formatNumber(row.net.amount, 2)}
                valueLabel={t('finance.balances.net')}
              />
            )}
            /* A row opens the record its `directoryId` names (S-22f) — never
               by the opaque `id`. A party linked to no record opens nothing and
               does not look as if it would: a row that looked clickable and did
               nothing would be worse than one that does not. */
            onRowClick={(row) => {
              const href = balanceHref(row)
              if (href) void navigate(href)
            }}
            isRowClickable={(row) => balanceHref(row) !== null}
            sorting={viewState.sorting}
            onSortingChange={controls.setSorting}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={(next) =>
              controls.setHiddenColumns(toHiddenColumns(next))
            }
          />
        </Card>
        )
      })}
    </div>
  )
}

function AgingPanel({ range }: { range: DateRange }) {
  const { t } = useTranslation()
  const { data, isPending, isError, error, refetch } = useAging(range)

  if (isPending) return <Skeleton className="h-48 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  /* Emptiness here is "nothing is outstanding", NOT an empty array. The server
     always emits all five buckets, the empty ones included — an aged bucket
     that holds nothing is a fact worth showing, not a row to omit — so a book
     with nothing on it arrives as five zero rows. Reading the length would
     leave the founder staring at five empty bars, and the *nothing yet* state
     it is supposed to reach would never fire. */
  const outstanding = data.some((bucket) => Number(bucket.amount.amount) !== 0)

  if (!outstanding) {
    return (
      <Card>
        <EmptyState
          title={t('finance.aging.emptyTitle')}
          description={t('finance.aging.emptyDescription')}
        />
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('finance.aging.title')}</CardTitle>
      </CardHeader>
      <ul className="flex flex-col px-[18px] pb-3">
        {data.map((bucket, index) => (
          <li
            key={bucket.id}
            className={cn(
              'flex items-center gap-3 py-3',
              index !== data.length - 1 && 'border-line-hair border-b',
            )}
          >
            {/* Coloured from `overdue`, never from a list of which keys are
                red: the server decides what "late" means, and a client that
                kept its own list would disagree with it the first time a
                bucket was added. */}
            <span
              aria-hidden
              className="size-2 shrink-0 rounded-full"
              style={{
                background: bucket.overdue
                  ? 'var(--color-negative)'
                  : 'var(--color-blue-500)',
              }}
            />
            {/* The label is translated from the bucket's id. A server-sent label
                would arrive in one language and print in the other — which is
                why the wire carries no label at all.
                `id`, not `key`: the fixture spelled this field `key` and the
                real read spells it `id`, so every one of these five bars was
                building `finance.aging.buckets.undefined` and printing the raw
                key back at the reader. The TOKENS did not move, so the
                catalogue's five entries are untouched. */}
            <span className="text-text-body w-36 text-[13px]">
              {t(`finance.aging.buckets.${bucket.id}`)}
            </span>

            <span className="bg-neutral-100 h-2 flex-1 overflow-hidden rounded-full">
              <span
                className="block h-full rounded-full"
                style={{
                  width: `${bucket.sharePercent}%`,
                  background: bucket.overdue
                    ? 'var(--color-negative)'
                    : 'var(--color-blue-500)',
                }}
              />
            </span>

            <span
              className={cn(
                'asa-num w-32 text-right text-[13px]',
                bucket.overdue ? 'text-negative font-semibold' : 'text-text-heading',
              )}
            >
              {formatMoney(bucket.amount.amount, bucket.amount.currency)}
            </span>
            <span className="asa-num text-text-muted w-10 text-right text-[12px]">
              {bucket.sharePercent}%
            </span>
          </li>
        ))}
      </ul>
    </Card>
  )
}

/* The payment log (Story 4.5).
 *
 * Two figures per row rather than one, because a payment has two states worth
 * distinguishing and only one of them used to be shown: what has been applied
 * to paper and what is still sitting there unapplied. Unallocated cash is
 * legal, is never auto-applied (AD-11), and is exactly what AC 4 requires to be
 * VISIBLE as unallocated — so it is coloured, weighted and given its own
 * column, and a fully applied payment goes quiet by comparison.
 *
 * A row opens onto its allocations rather than onto an edit sheet. There is no
 * amend-a-payment route in this story, and what a reader actually wants from a
 * payment row is what it settled.
 */
const paymentColumn = createColumnHelper<Payment>()

const PAYMENT_COLUMN_MENU: readonly {
  id: string
  labelKey: ParseKeys
  locked?: boolean
}[] = [
  { id: 'reference', labelKey: 'finance.payments.reference', locked: true },
  { id: 'counterpartyName', labelKey: 'finance.balances.counterparty' },
  { id: 'receivedOn', labelKey: 'finance.payments.received' },
  { id: 'amount', labelKey: 'common.fields.amount' },
  { id: 'allocated', labelKey: 'finance.payments.allocated' },
  { id: 'unallocated', labelKey: 'finance.payments.unallocated' },
  { id: 'status', labelKey: 'finance.payments.status' },
]

const PAYMENT_LOCKED: readonly string[] = PAYMENT_COLUMN_MENU.filter(
  (entry) => entry.locked,
).map((entry) => entry.id)

const PAYMENT_DIRECTIONS: readonly Payment['direction'][] = ['in', 'out']

/** "Nothing selected", shared so the memo below does not recompute per paint. */
const NO_FILTER: readonly string[] = []

function PaymentsPanel({
  range,
  pendingOnly,
  onPendingOnlyChange,
  unallocatedOnly = false,
  onUnallocatedOnlyChange,
}: {
  range: DateRange
  /** `?pending=1`: the confirmation queue instead of the ranged log. */
  pendingOnly: boolean
  onPendingOnlyChange: (next: boolean) => void
  /** `?unallocated=1` (S-12): only outbound payments with an unallocated
   *  remainder, asked of the server as `unallocated=out`. */
  unallocatedOnly?: boolean
  onUnallocatedOnlyChange?: (next: boolean) => void
}) {
  const { t } = useTranslation()
  const format = useFormat()
  const { data: session } = useSession()
  /* The queue is offered only to a reader who works it — the same predicate
     that decides whether the pending query is made at all. Anybody else who
     follows a `pending=1` link reads the ordinary log rather than an empty
     queue that was never asked for. */
  const worksQueue = canWorkPaymentQueue(session)
  const queueOn = pendingOnly && worksQueue
  const pending = usePendingPayments()
  /* The queue wins when both are in the URL: it is the narrower working list,
     and its chip is the one on screen. */
  const unallocatedOn = unallocatedOnly && !queueOn
  const ranged = usePayments(range, { enabled: !queueOn, unallocatedOut: unallocatedOn })
  const { data, isPending, isError, error, refetch } = queueOn ? pending : ranged
  /* «Внести платёж» is `finance.payment.record`'s. The register is read by
     more roles than may write to it, and a reader without the capability is
     not offered the act at all — not a greyed button, and not a sheet that ends
     in a 403 (QW-7). The server re-checks on the request (AD-21). */
  const mayRecord = can(session, CAP.financePaymentRecord)
  const [recording, setRecording] = useState(false)
  /** The one payment whose allocations are open, as a SHEET rather than an
   *  in-place expansion: UX-DR-4 is explicit that a row click opens the record
   *  and never expands in place, and the disclosure this replaces was the
   *  criterion's own counter-example sitting on the money page. */
  const [openPaymentId, setOpenPaymentId] = useState<string | null>(null)
  /** The SERVER's latest answer about the open payment — the row returned by
   *  the last write made from its sheet (a confirm, an allocation). In the
   *  pending queue a confirmed payment leaves the list on the refetch, and the
   *  sheet is drawn from the list; without this it closed under the reader and
   *  the confirmation was never seen (AC16). Replaced on EVERY write, so an
   *  allocation made after the confirm moves the figures too. */
  const [serverRow, setServerRow] = useState<Payment | null>(null)
  const closePayment = () => {
    setOpenPaymentId(null)
    setServerRow(null)
  }
  const record = useRecordPayment()
  const [viewState, controls] = useViewState('finance.payments')
  /* «Ждут подтверждения» is `?pending=1` in the URL (QW-10), owned by the page
     and handed down — not local state, and not the persisted view state beside
     the direction facet. Not persisted, because it is a WORKING queue somebody
     empties in one sitting rather than a way of looking at the log they want
     to find still on next week. In the URL, because it is a PLACE: the
     dashboard's "N payments await confirmation" row links straight to it, and
     so can a colleague. */

  const directionFilter = viewState.filters.direction ?? NO_FILTER

  /* How many records are still waiting on Finance (0059), from the server's
     ALL-TIME queue (`status=pending&range=all`) rather than counted over the
     loaded rows: a payment booked last quarter and never confirmed is exactly
     the one a count over the date range would hide. It is the query the
     sidebar's Finance badge reads, so the chip and the badge cannot disagree.
     Independent of the direction facet, so narrowing to outgoing payments does
     not quietly change what the counter claims. */
  const awaiting = pending.data?.length ?? 0

  /* The status is the SERVER's filter now — the queue arrives as the pending
     rows and nothing here filters it again. The direction facet is still
     applied CLIENT-SIDE over rows the server already scoped. That direction is
     the whole rule (AD-5, AD-21): a facet here can only narrow a set the scope
     predicate fixed, and nothing it holds is forwarded as a query parameter. */
  const rows = useMemo(() => {
    const all = data ?? []
    if (directionFilter.length === 0) return all
    return all.filter((payment) => directionFilter.includes(payment.direction))
  }, [data, directionFilter])

  const columnVisibility = useMemo(
    () =>
      toVisibilityState(
        viewState.hiddenColumns.filter((id) => !PAYMENT_LOCKED.includes(id)),
      ),
    [viewState.hiddenColumns],
  )

  const columns = useMemo(
    () => [
      paymentColumn.accessor('reference', {
        header: t('finance.payments.reference'),
        size: 196,
        cell: (info) => (
          <span className="flex items-center gap-2">
            <span className="text-text-heading font-mono text-[12.5px]">
              {info.getValue()}
            </span>
            <Tag tone={info.row.original.direction === 'in' ? 'brand' : 'neutral'}>
              {t(`finance.payments.${info.row.original.direction}`)}
            </Tag>
          </span>
        ),
      }),
      paymentColumn.accessor('counterpartyName', {
        header: t('finance.balances.counterparty'),
        cell: (info) => info.getValue(),
      }),
      paymentColumn.accessor('receivedOn', {
        header: t('finance.payments.received'),
        size: 116,
        cell: (info) => format.date(info.getValue()),
      }),
      paymentColumn.accessor((row) => row.amount.amount, {
        id: 'amount',
        header: t('common.fields.amount'),
        size: 132,
        meta: { numeric: true },
        sortingFn: byDecimal<Payment>((row) => row.amount.amount),
        cell: (info) => (
          <>
            {formatNumber(info.getValue(), 2)}
            <span className="text-text-muted ml-1 text-[11.5px] font-normal">
              {info.row.original.amount.currency}
            </span>
          </>
        ),
      }),
      paymentColumn.accessor((row) => row.allocated.amount, {
        id: 'allocated',
        header: t('finance.payments.allocated'),
        size: 124,
        meta: { numeric: true },
        sortingFn: byDecimal<Payment>((row) => row.allocated.amount),
        cell: (info) => formatNumber(info.getValue(), 2),
      }),
      paymentColumn.accessor((row) => row.unallocated.amount, {
        id: 'unallocated',
        header: t('finance.payments.unallocated'),
        size: 132,
        meta: { numeric: true },
        sortingFn: byDecimal<Payment>((row) => row.unallocated.amount),
        /* Unallocated cash is legal and visible; it is never auto-applied
           (AD-11). Showing it — and weighting it — is the point. */
        cell: (info) => (
          <span
            className={cn(
              Number(info.getValue()) > 0
                ? 'text-attention font-semibold'
                : 'text-text-muted',
            )}
          >
            {formatNumber(info.getValue(), 2)}
          </span>
        ),
      }),
      /* The record's status, in its long form here and as a badge on the row's
         reference. It qualifies NOTHING in the columns beside it: a pending
         payment's amount, allocated and unallocated figures are the same
         figures a confirmed one's are (spec L4). */
      paymentColumn.accessor('status', {
        id: 'status',
        header: t('finance.payments.status'),
        size: 152,
        cell: (info) =>
          info.getValue() === 'pending' ? (
            <Tag tone="attention">{t('finance.payments.statuses.pending')}</Tag>
          ) : (
            <span className="text-text-muted">
              {t('finance.payments.statuses.confirmed')}
            </span>
          ),
      }),
    ],
    [t, format],
  )

  if (isPending) return <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  /* Read from the LIST rather than held in state, so the sheet shows the row the
     server most recently confirmed. Allocating invalidates the payment queries,
     the list refetches, and the open sheet's figures move with it — held in
     state it would keep showing the balance as it was before the allocation it
     just made.

     The one fallback is a payment that has left the list while its sheet is
     open — the pending queue drops it on the refetch after a confirm. Then no
     list row can follow it any more, so the sheet is drawn from `serverRow`:
     the row the server answered to the LAST write made from this sheet. The
     confirm sets it, and so does every allocation after it, so the allocations
     and the unallocated figure move exactly as they would from the list; it
     is never a copy the client edited. The sheet stays on it until the reader
     closes it, so «Платёж подтверждён» is seen where it was earned. A REJECTED
     payment answers with nothing and still closes the sheet. */
  const openPayment =
    rows.find((payment) => payment.id === openPaymentId) ??
    (serverRow && serverRow.id === openPaymentId ? serverRow : undefined)

  return (
    <Card>
      <CardHeader
        action={
          <span className="flex flex-wrap items-center gap-2">
            <FacetFilter
              label={t('finance.payments.directionLabel')}
              options={toOptions(PAYMENT_DIRECTIONS, (direction) =>
                t(`finance.payments.${direction}`),
              )}
              selected={directionFilter}
              onChange={(values) => controls.setFilter('direction', values)}
            />
            {/* The queue, offered only when there IS one: a chip counting zero
                on every desk with nothing outstanding is noise, and the number
                is the whole reason the chip is worth a place in the header.
                Still offered while the queue is OPEN and emptied under the
                reader, because a filter that cannot be released is a trap. */}
            {worksQueue && (awaiting > 0 || queueOn) ? (
              <Button
                variant={queueOn ? 'primary' : 'secondary'}
                size="sm"
                aria-pressed={queueOn}
                onClick={() => onPendingOnlyChange(!queueOn)}
              >
                {t('finance.payments.pendingFilter', { count: awaiting })}
              </Button>
            ) : null}
            <ColumnsMenu
              columns={PAYMENT_COLUMN_MENU.map((entry) => ({
                id: entry.id,
                label: t(entry.labelKey),
                locked: entry.locked,
              }))}
              hidden={viewState.hiddenColumns}
              onToggle={controls.toggleColumn}
              onReset={() => controls.setHiddenColumns([])}
            />
            {/* Offered only while the narrowing is ON (S-12): it arrives from
                the balances banner's «Показать», and a filter that cannot be
                released is a trap. There is no count to show without it. */}
            {unallocatedOn ? (
              <Button
                variant="primary"
                size="sm"
                aria-pressed
                onClick={() => onUnallocatedOnlyChange?.(false)}
              >
                {t('finance.payments.unallocatedOutFilter')}
              </Button>
            ) : null}
            {mayRecord ? (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  // The last attempt's refusal belongs to the last attempt: the
                  // sheet words it under its fields (frontend/README).
                  record.reset()
                  setRecording(true)
                }}
              >
                {t('finance.payments.record')}
              </Button>
            ) : null}
          </span>
        }
      >
        <CardTitle>{t('finance.payments.title')}</CardTitle>
      </CardHeader>

      {/* The page's range control above still shows its own period while the
          queue is open, so the queue says in words that it is not bound by it
          — otherwise "3M" over a list holding last year's payment reads as a
          filter that is not working. */}
      {queueOn ? (
        <p className="text-text-muted px-[18px] pb-2 text-[12.5px] leading-snug font-light">
          {t('finance.payments.pendingAllPeriods')}
        </p>
      ) : null}

      <RecordList
        label={t('finance.payments.title')}
        data={rows}
        columns={columns}
        getRowId={(payment) => payment.id}
        onRowClick={(payment) => setOpenPaymentId(payment.id)}
        renderCard={(payment) => (
          <RecordCard
            id={payment.reference}
            status={
              <span className="flex items-center gap-1.5">
                <Tag tone={payment.direction === 'in' ? 'brand' : 'neutral'}>
                  {t(`finance.payments.${payment.direction}`)}
                </Tag>
                <PaymentStatusTag status={payment.status} />
              </span>
            }
            primary={payment.counterpartyName}
            secondary={format.date(payment.receivedOn)}
            value={formatNumber(payment.unallocated.amount, 2)}
            valueLabel={t('finance.payments.unallocated')}
          />
        )}
        sorting={viewState.sorting}
        onSortingChange={controls.setSorting}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={(next) =>
          controls.setHiddenColumns(toHiddenColumns(next))
        }
        /* The two facts UX-DR-17 keeps apart, and the second condition is what
           keeps them apart: a facet over an already-empty log narrowed nothing,
           so a first-day Finance user meets "no payments recorded" rather than
           "nothing matches" and a Clear button that reveals an empty list. It
           reads the FACET only — the date range has its own control and no "all"
           option, so offering to clear it would offer a button that cannot do
           what it says.

           Passed as a NODE rather than through DataTable's own `filtered` prop,
           which `RecordList` does not forward — see the note at the top of this
           file. The node reaches both halves, so the phone card list makes the
           same distinction the table does. */
        empty={
          directionFilter.length > 0 && (data ?? []).length > 0 ? (
            <TableEmptyState
              filtered
              onClearFilters={() => {
                controls.clearFilters()
                onPendingOnlyChange(false)
                onUnallocatedOnlyChange?.(false)
              }}
            />
          ) : unallocatedOn ? (
            /* Everything allocated is good news too, and a different fact from
               an empty register. */
            <EmptyState
              title={t('finance.payments.unallocatedOutEmptyTitle')}
              description={t('finance.payments.unallocatedOutEmptyDescription')}
            />
          ) : queueOn ? (
            /* The emptied queue is good news, said plainly — not "no payments
               recorded", which is a different fact about a different list. */
            <EmptyState
              title={t('finance.payments.pendingEmptyTitle')}
              description={t('finance.payments.pendingEmptyDescription')}
            />
          ) : (
            <EmptyState
              title={t('finance.payments.emptyTitle')}
              description={t('finance.payments.emptyDescription')}
            />
          )
        }
      />

      {/* Closed from onSuccess, so a refusal leaves the sheet open with the
          draft intact rather than closing over a save that never happened.
          Mounted only for a reader who may record, like its trigger. */}
      {mayRecord ? (
        <PaymentFormSheet
          open={recording}
          onClose={() => setRecording(false)}
          pending={record.isPending}
          /* The raw refusal: the sheet words it in the banner and puts a
             party code (S-22d2) under the party field. */
          failure={record.isError ? record.error : undefined}
          onSubmit={(draft) =>
            record.mutate(toPaymentPayload(draft), {
              onSuccess: () => setRecording(false),
            })
          }
        />
      ) : null}

      {/* Gated on the ROW rather than on the id, like the approval sheet: a row
          that vanished from the list under a refetch has no figures to render,
          and a sheet open over `undefined` is how a screen shows a payment that
          is no longer there. */}
      {openPayment ? (
        <Sheet
          open
          onClose={closePayment}
          title={t('finance.payments.detailTitle', {
            reference: openPayment.reference,
          })}
        >
          {/* The facts AC 1 requires a payment to carry and the scannable row
              cannot hold: how the money moved, which of OUR entities it moved
              through, and the booking the server took at the case's basis
              (AD-9). The USD figure is read, never recomputed here. */}
          {/* Which shipment the money moved against (QW-14). Present only when
              the reader's scope reaches the case; see PaymentCaseLink. */}
          <PaymentCaseLink caseId={openPayment.caseId} />

          <div className="text-text-muted flex flex-wrap gap-x-5 gap-y-1 text-[12.5px] font-light">
            <span>{t(`finance.payments.methods.${openPayment.method}`)}</span>
            <span>{openPayment.issuingEntityName}</span>
            {/* A USD payment drops the rate clause: «Курс 1.0000» restates the
                currency and reads as a conversion that never happened. The
                branch compares the currency CODE; nothing is computed (F-2). */}
            <span className="asa-num">
              {openPayment.amount.currency === 'USD'
                ? formatMoney(openPayment.usd, 'USD')
                : t('finance.payments.bookedAt', {
                    rate: openPayment.appliedRate,
                    date: format.date(openPayment.rateDate),
                    usd: formatNumber(openPayment.usd, 2),
                  })}
            </span>
          </div>

          <PaymentAllocations payment={openPayment} onServerRow={setServerRow} />
        </Sheet>
      ) : null}
    </Card>
  )
}

function ApprovalsPanel() {
  const { t } = useTranslation()
  const format = useFormat()
  const { data, isPending, isError, error, refetch } = useApprovals()
  const decide = useDecideApproval()
  /* The request being confirmed for approval. Rejection acts directly: a
     rejected request can be re-raised, an approved rate is binding. */
  const [confirming, setConfirming] = useState<ApprovalRequest | null>(null)

  if (isPending) return <Skeleton className="h-48 rounded-[var(--radius-DEFAULT)]" />
  if (isError) return <ErrorState error={error} onRetry={() => void refetch()} />

  if (data.length === 0) {
    return (
      <Card>
        <EmptyState
          title={t('finance.approvals.emptyTitle')}
          description={t('finance.approvals.emptyDescription')}
        />
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-3.5">
      {data.map((approval) => {
        const raise = Number(approval.delta.amount) > 0
        return (
          <Card key={approval.id}>
            <div className="flex flex-col gap-3 p-[18px]">
              <div className="flex flex-wrap items-center gap-2">
                <NavLink
                  to={`/cases/${approval.caseId}`}
                  className="text-brand text-[13px] font-semibold"
                >
                  {approval.caseNumber}
                </NavLink>
                <StatusPill tone="attention">{t('finance.approvals.pending')}</StatusPill>
                <span className="text-text-muted text-[12.5px] font-light">
                  {approval.requesterName} · {format.timestamp(approval.requestedAt)}
                </span>
              </div>

              {/* The requested change alongside the current value, so the
                  requester is never left guessing whether it was seen. */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <span className="flex flex-col gap-1">
                  <span className="asa-eyebrow">{t('finance.approvals.current')}</span>
                  <span className="asa-num text-text-body text-[15px]">
                    {formatMoney(
                      approval.currentRate.amount,
                      approval.currentRate.currency,
                    )}
                  </span>
                </span>
                <span className="text-text-faint" aria-hidden>
                  →
                </span>
                <span className="flex flex-col gap-1">
                  <span className="asa-eyebrow">{t('finance.approvals.requested')}</span>
                  <span className="asa-num text-text-heading text-[15px] font-semibold">
                    {formatMoney(
                      approval.requestedRate.amount,
                      approval.requestedRate.currency,
                    )}
                  </span>
                </span>
                <span className="flex flex-col gap-1">
                  <span className="asa-eyebrow">{t('finance.approvals.change')}</span>
                  <span
                    className={cn(
                      'asa-num text-[15px] font-semibold',
                      raise ? 'text-positive' : 'text-negative',
                    )}
                  >
                    {formatMoney(approval.delta.amount, approval.delta.currency)}
                  </span>
                </span>
              </div>

              <p className="text-text-body border-line-hair border-t pt-2.5 text-[13px] leading-snug font-light">
                {approval.reason}
              </p>

              <div className="flex gap-2">
                {/* Approving a rate change asks first: it is financially
                    binding, so it confirms rather than offering undo. */}
                <Button
                  size="sm"
                  onClick={() => setConfirming(approval)}
                  disabled={decide.isPending}
                >
                  {t('finance.approvals.approve')}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() =>
                    decide.mutate({
                      id: approval.id,
                      caseId: approval.caseId,
                      decision: 'reject',
                    })
                  }
                  disabled={decide.isPending}
                >
                  {t('finance.approvals.reject')}
                </Button>
              </div>

              {decide.isError && decide.variables?.id === approval.id ? (
                <p className="text-negative text-[12.5px]">
                  {refusalMessage(decide.error, t)}
                </p>
              ) : null}
            </div>
          </Card>
        )
      })}

      {/* Keyed on nothing and gated on the record, like the credential dialog:
          a null record has no title to render, so the sheet simply is not
          there rather than open-with-empty-props. */}
      {confirming ? (
        <Sheet
          open
          onClose={() => setConfirming(null)}
          title={t('finance.approvals.confirmTitle', {
            number: confirming.caseNumber,
          })}
          footer={
            <>
              <Button
                className="w-full"
                disabled={decide.isPending}
                onClick={() =>
                  decide.mutate(
                    {
                      id: confirming.id,
                      caseId: confirming.caseId,
                      decision: 'approve',
                    },
                    { onSuccess: () => setConfirming(null) },
                  )
                }
              >
                {t('finance.approvals.approve')}
              </Button>
              <Button
                className="w-full"
                variant="secondary"
                onClick={() => setConfirming(null)}
              >
                {t('common.actions.cancel')}
              </Button>
            </>
          }
        >
          <p className="text-text-body text-[13px] leading-relaxed">
            {t('finance.approvals.confirmBody', {
              number: confirming.caseNumber,
              current: formatMoney(
                confirming.currentRate.amount,
                confirming.currentRate.currency,
              ),
              requested: formatMoney(
                confirming.requestedRate.amount,
                confirming.requestedRate.currency,
              ),
            })}
          </p>
        </Sheet>
      ) : null}
    </div>
  )
}


export function FinancePage() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  /* The date range lives in the URL beside the tab, and for the same reason: a
     filtered view is a place somebody sends a colleague a link to, and a range
     held only in component state makes "receivables over the last year" a thing
     you can look at but not send. `?range=` therefore arrives from outside and
     may say anything — including `1W` from a bookmark taken before the
     vocabulary was reconciled — so it goes through `isDateRange` rather than
     being cast; an unknown value falls back to the default period instead of
     asking the server for one it does not implement. */
  const requestedRange = searchParams.get('range')
  const range: DateRange = isDateRange(requestedRange) ? requestedRange : '3M'

  const requested = searchParams.get('tab')
  const tab: TabKey = TABS.some((t) => t.key === requested)
    ? (requested as TabKey)
    : 'balances'

  /* Which case's paper the Documents tab is showing (Story 5.4, issue #27). In
     the URL beside the tab, and for the same reason: a document belongs to a
     case, so "the documents on MG-19" is a place somebody sends a colleague a
     link to. Unlike the date range it is NOT page-level — the other four tabs
     are cross-case surfaces and a case filter would mean something different on
     each of them.

     Without `?case=` the tab shows the company-wide register of issued paper
     (`GET /api/finance/issued-documents`, #137 N-30); `?case=` narrows it to one
     case's paper, drafts included, which the register never lists. */
  const documentsCaseId = searchParams.get('case') ?? ''

  /* The payment-confirmation queue (QW-10), `?pending=1`. In the URL because
     «Мои задачи»'s `payments-to-confirm` card leads Finance here (S-2b.2; it
     sits in the strip above the dashboard too), and a link has to land on the
     queue rather than on the log with a chip still to find. Only the literal
     `1` opens it; anything else is the ordinary log. */
  const pendingOnly = searchParams.get('pending') === '1'

  /* The unallocated outbound payments (S-12), `?unallocated=1` — the balances
     banner's «Показать» lands here. Only the literal `1` narrows; the payments
     panel asks the server for `unallocated=out`, the way `pending=1` becomes
     `status=pending`. */
  const unallocatedOnly = searchParams.get('unallocated') === '1'

  /* A reader without `finance.receivables.read` gets ONE refusal and nothing
     else (N-21). Every read on this page — balances, aging, payments,
     approvals, the issued-paper register — sits behind that single capability
     (`crossCaseFinance`), so a deep link used to render the heading, five tabs
     and the period buttons and then a «Недоступно для вашей роли» card. The
     sidebar already hides the entry; this is the typed address. Gated on the
     capability, never on a role name: roles are runtime records. The server
     re-checks every request (AD-21). While the session loads nobody is
     permitted yet, so a skeleton stands in rather than the refusal flashing at
     a Finance reader. */
  const { data: session, isPending: sessionPending } = useSession()
  const worksQueue = canWorkPaymentQueue(session)

  if (sessionPending) {
    return (
      <>
        <PageHeader title={t('finance.title')} subtitle={t('finance.subtitle')} />
        <Skeleton className="h-64 rounded-[var(--radius-DEFAULT)]" />
      </>
    )
  }

  if (!can(session, CAP.financeReceivablesRead)) {
    return (
      <>
        <PageHeader title={t('finance.title')} subtitle={t('finance.subtitle')} />
        <Card>
          <EmptyState
            title={t('finance.noAccess.title')}
            description={t('finance.noAccess.description')}
          />
        </Card>
      </>
    )
  }

  /* The period only means something where the tab reads by it. It filters the
     balances, aging, the payment LOG and the issued-paper register; «Согласования
     ставок» has no dates to narrow, the payment-confirmation queue is all-time
     by design (QW-10), and a chosen case's paper is read per case, in full.
     A control that does nothing is a control that lies (N-30). */
  const rangeApplies =
    tab === 'balances' ||
    tab === 'aging' ||
    (tab === 'payments' && !(pendingOnly && worksQueue)) ||
    (tab === 'documents' && documentsCaseId === '')

  return (
    <>
      <PageHeader
        title={t('finance.title')}
        subtitle={t('finance.subtitle')}
        actions={
          rangeApplies ? (
            <DateRangeControl
              value={range}
              onChange={(next) => {
                const params = new URLSearchParams(searchParams)
                params.set('range', next)
                // `replace`, like the tab: narrowing the period is refining the
                // view you are already on, and a Back button that walked back
                // through every period somebody tried is one nobody uses.
                setSearchParams(params, { replace: true })
              }}
            />
          ) : undefined
        }
      />

      <TabNav
        label={t('finance.sectionsLabel')}
        items={TABS}
        value={tab}
        onChange={(key) => {
          const next = new URLSearchParams(searchParams)
          next.set('tab', key)
          setSearchParams(next, { replace: true })
        }}
      />

      {/* Keyed on the tab only, not on the range: changing the date range
          refreshes the numbers inside the panel that is already open, and
          replaying an entrance for that would read as a navigation the user
          did not make. */}
      <TabPanel tabKey={tab}>
        {tab === 'balances' ? <BalancesPanel range={range} /> : null}
        {tab === 'aging' ? <AgingPanel range={range} /> : null}
        {tab === 'payments' ? (
          <PaymentsPanel
            range={range}
            pendingOnly={pendingOnly}
            onPendingOnlyChange={(next) => {
              const params = new URLSearchParams(searchParams)
              if (next) params.set('pending', '1')
              else params.delete('pending')
              // `replace`, like the range: opening the queue is refining the
              // view you are on, not a page Back should walk through.
              setSearchParams(params, { replace: true })
            }}
            unallocatedOnly={unallocatedOnly}
            onUnallocatedOnlyChange={(next) => {
              const params = new URLSearchParams(searchParams)
              if (next) params.set('unallocated', '1')
              else params.delete('unallocated')
              setSearchParams(params, { replace: true })
            }}
          />
        ) : null}
        {tab === 'approvals' ? <ApprovalsPanel /> : null}
        {tab === 'documents' ? (
          <DocumentsPanel
            caseId={documentsCaseId}
            range={range}
            onSelectCase={(caseId) => {
              const next = new URLSearchParams(searchParams)
              // '' is "all cases": the register, with no `case` in the address.
              if (caseId === '') next.delete('case')
              else next.set('case', caseId)
              // `replace`, like the tab: choosing a case is refining the view
              // you are on, and a Back button that walked back through every
              // case somebody tried is a Back button nobody uses.
              setSearchParams(next, { replace: true })
            }}
          />
        ) : null}
      </TabPanel>
    </>
  )
}
